import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmptyAny, isDef } from '@lhb/func';
import {
  calculateHandle,
  ControlType,
  echoEstimateSalesComYHT,
  echoEstimateSalesConclusionComYHT,
  echoPassengerFlowCom,
  formDataComsToTiled,
  formDataEcho,
  getCalculateComs,
  parseValueCatch,
  relevancyShow,
  setComValue,
} from '@/common/components/business/DynamicComponent/config';
import { pcdIdentification, addressIdentification, } from '@/common/components/business/DynamicComponent/customize';
import cs from 'classnames';
import styles from './index.module.less';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Container from '@/common/components/Data/V2Container';
import V2Tabs from '@/common/components/Data/V2Tabs';
import Main from './Main';
import moment from 'moment';
import { radioEnum } from '@/common/components/business/DynamicComponent/components/Footprint';
import { getFormDetail, getTemplateDetail } from '@/common/api/expandStore/chancepoint';
import ClusterHintModal from './ClusterHintModal';
import { useBigdataFn } from '@/common/utils/bigdata';
import { handleJNPValue } from '@/common/components/business/DynamicComponent/components/CalculationJNP/components/transfer2form';
import Header from './components/Header';
import Footer from './components/Footer';

/**
 * 创建/机会点时的表单
 * @Description 逻辑说明：
 * 1. 利用antd的Form组件的onValuesChange监听输入的变化
 * 2. 从接口获取到的组件数据，通过递归将所有组件平铺到一个对象数组中
 * 3. 根据每个表单项绑定的name（即identification），在平铺的组件对象中查找对应的组件，然后进行textValue的赋值
 */
const ChanceCreateDrawer: FC<{
  chancepointAlias?,
  /** 表单抽屉所需数据 */
  drawerData: {
    open,
    /** 模板id */
    templateId,
    /** 编辑时的id */
    id,
    /** 权限，目前看只有鱼你项目会传：src/common/components/business/Fishtogether/PointDetail/components/Detail/Header.tsx */
    importPermission?,
    /** 拓店任务相关信息 */
    expandStoreData?,
  },
  closeHandle,
  onSearch,
  update,
  assignedData?,
  /** 创建后需要与机会点关联 */
  needAssiocateChance?,
  /** 拓店任务id，以后可能会用到 */
  // expansionTaskId?,
  /** 拓店任务商圈id */
  expansionTaskClusterId?,
  /** 是否拓店任务-点位评估进入 */
  isExpandStoreTaskIn?: boolean,
  /** 拓店任务-提交点位评估 */
  handleExpandStoreApproved?,
}> = ({
  chancepointAlias,
  drawerData,
  closeHandle,
  onSearch,
  update,
  assignedData,
  needAssiocateChance, // 创建后需要与机会点关联
  // expansionTaskId,
  expansionTaskClusterId,
  isExpandStoreTaskIn,
  handleExpandStoreApproved
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook

  /*
    是否点位评估表单，是的话字段参照拓店模板设置，而不是流程配置
    请求formDetail后，按照有无绑定taskId来决定，有则true
    默认值必须是false，新建机会点时不请求formDetail，会使用默认值
  */
  const [isEvaluation, setIsEvaluation] = useState<boolean>(false);

  const tiledFormDataRef = useRef<any[]>([]); // 所有组件字段平铺后的对象数组
  const saveSucceedRef = useRef(false); // 已保存成功
  const topRef: any = useRef(); // 吸顶部分
  const tabActiveRef: any = useRef();
  const tabContentRefs: any = useRef([]);
  const [form] = Form.useForm();
  const { open, templateId, id, importPermission } = drawerData;
  const [formData, setFormData] = useState<any>({}); // 接口返回的动态表单源数据
  const [tabActive, setTabActive] = useState<string>(''); // 当前tab值
  // supportDirectApproval 是否支持直接提交审批 1:支持 2:不支持
  const [supportDirectApproval, setSupportDirectApproval] = useState<number>(2);
  const [refreshCurrentDrawer, setRefreshCurrentDrawer] = useState<number>(0);
  const [isFootprintValue, setIsFootprintValue] = useState<boolean>(false);// 编辑状态下踩点任务是否已创建
  const [allComps, setAllComps] = useState<any[]>([]);
  // const [scrollAccordingTab, setScrollAccordingTab] = useState<boolean>(false); // 是否按照tab点击滚动页面，第一次点击tab后才为true，避免初始化内容后就滚动
  const [clusterHintModalOpen, setClusterHintModalOpen] = useState<boolean>(false);
  const [isAreaProtect, setIsAreaProtect] = useState<boolean>(false);// 区域保护是否存在

  useEffect(() => {
    // 打开抽屉时重置标记
    open && (saveSucceedRef.current = false);
  }, [open]);

  useEffect(() => {
    // 编辑
    if (id) {
      getDetail();
      return;
    }
    // 创建时获取模板详情
    if (templateId) {
      getTempleteDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, templateId, refreshCurrentDrawer]);

  // 一级菜单
  const tabs = useMemo(() => {
    if (!Object.keys(formData).length) return [];
    const { propertyGroupVOList } = formData;
    if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
      return propertyGroupVOList.map((item: any) => ({
        label: item.name,
        key: `${item.id}`, // tabs组件的activeKey是string类型
      }));
    }
    return [];
  }, [formData]);

  /**
   * @description 是否以长列表展示，总字段数<=120时使用
   */
  const isLongList = useMemo(() => {
    const res = isArray(allComps) && allComps.length <= 120;
    return res;
  }, [allComps]);

  const {
    getTempleteDetail,
    getDetail,
    addressComWhenEdit,
    tabChange,
    closeDrawer,
    confirmClose,
    handleFootprintValue,
    computationalLogic,
    ...methods
  } = useMethods({

    /** 获取详情 */
    getDetail() {
      getFormDetail({ id }).then((res: any) => {
        // dynamicRelationId需要特殊赋值
        const { templateDetailResponse, dynamicRelationId, supportDirectApproval, taskId, protectInfo } = res;
        setIsEvaluation(!!taskId);

        templateDetailResponse.dynamicRelationId = dynamicRelationId;
        const { propertyGroupVOList } = templateDetailResponse;
        // 设置tabs默认值
        if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
          setTabActive(`${propertyGroupVOList[0].id}`);
          tabActiveRef.current = `${propertyGroupVOList[0].id}`;
          formDataComsToTiled(propertyGroupVOList, tiledFormDataRef.current); // 将组件对象平铺到数组中
          // 表单赋值
          const allComs = tiledFormDataRef.current;
          // 这里应该是设置错了，但是目前没接到反馈，怕贸然改动会影响到
          setAllComps(allComps);
          addressComWhenEdit(allComs); // 特殊逻辑，拿到省市区组件的值后，将城市信息塞到地址组件中
          relevancyShow(allComs); // 关联显示
          const values = {};
          allComs.forEach((item: any) => {
            const { identification, controlType } = item;
            // 根据组件类型，将表单数据set到form上
            values[identification] = formDataEcho(item);
            // 设置踩点组件数据回显
            handleFootprintValue(item);
            handleJNPValue(item, form);
            if (controlType === ControlType.ESTIMATE_SALES_YHT.value) {
              echoEstimateSalesComYHT(parseValueCatch(item), form);
            } else if (controlType === ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value) {
              echoEstimateSalesConclusionComYHT(parseValueCatch(item), form);
            } else if (controlType === ControlType.PASSENGER_FLOW.value) {
              echoPassengerFlowCom(parseValueCatch(item), form);
            }
          });
          form.setFieldsValue(values);
        }
        setSupportDirectApproval(supportDirectApproval);
        setFormData(templateDetailResponse);
        setIsAreaProtect(!!protectInfo?.approvalId);
      });
    },
    addressComWhenEdit(allComs: any[]) {
      // 编辑时的地址组件的特殊逻辑处理
      const pcdCom = allComs.find((itemCom: any) => itemCom.identification === pcdIdentification);
      const addressCom = allComs.find((itemCom: any) => itemCom.identification === addressIdentification);
      if (!(pcdCom && addressCom)) return;
      const pcdVal = parseValueCatch(pcdCom);
      const addressVal = parseValueCatch(addressCom);
      if (!pcdVal) return;
      if (addressVal) {
        addressCom.textValue = JSON.stringify(Object.assign({}, addressVal, pcdVal));
      }
    },
    getTempleteDetail() {
      // 创建时的踩点任务必然是没有的
      setIsFootprintValue(false);
      // 获取模板详情
      getTemplateDetail({ id: templateId }).then((data: any) => {
        setIsEvaluation(false);
        const { propertyGroupVOList, supportDirectApproval } = data;
        // 设置tabs默认值
        if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
          setTabActive(`${propertyGroupVOList[0].id}`);
          formDataComsToTiled(propertyGroupVOList, tiledFormDataRef.current); // 将组件对象平铺到数组中
          isArray(assignedData) && assignedData.length && methods.specificAssignedData();
          setAllComps(tiledFormDataRef.current);

          const allComs = tiledFormDataRef.current;
          const values = {};
          allComs.forEach((item: any) => {
            const { identification, templateRestriction } = item;
            const defaultValue = JSON.parse(templateRestriction)?.defaultValue;
            // console.log('defaultValue', defaultValue, setComValue(item, defaultValue));
            // 根据组件类型，将表单数据set到form上
            isDef(defaultValue) ? setComValue(item, defaultValue) : undefined; // 将defaultValue赋给textValue
            values[identification] = formDataEcho(item);
          });
          relevancyShow(tiledFormDataRef.current); // 关联显示
          form.setFieldsValue(values);
          computationalLogic();
        }
        setFormData(data);
        setSupportDirectApproval(supportDirectApproval);
      });
    },
    /** 特殊的赋值逻辑，目前是客流计数创建机会点时自动带入几个字段 */
    specificAssignedData: () => {
      const values: any = {};
      assignedData.forEach((item: any) => {
        tiledFormDataRef.current.forEach((targetItem: any) => {
          if (item.identification === targetItem.identification) {
            targetItem.textValue = item.textValue;
            values[item.identification] = formDataEcho(targetItem);
          }
        });
      });
      form.setFieldsValue(values);
    },
    tabChange(active: string) {
      // setScrollAccordingTab(true);
      setTabActive(active);
      tabActiveRef.current = active;
      const targetEle = tabContentRefs.current?.find((refItem) => refItem?.id === +active);
      // 注意这里的behavior不要设置为smooth，会影响子组件中的handleScroll逻辑
      targetEle?.el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    confirmClose() {
      form.resetFields(); // 表单清空
      closeHandle();
      tiledFormDataRef.current = [];
      setAllComps([]);
    },
    closeDrawer() {
      // 关闭弹窗时
      V2Confirm({
        onSure: () => {
          confirmClose();
          bigdataFn('f9adcbfc-ff89-4ef0-84b0-a067d7307900', '创建机会点详情-取消', '点击了创建机会点详情-取消', { op_block: '创建机会点', op_event: ``, });
        },
        zIndex: 1010,
        title: '操作提示',
        content: '退出后将不保存当前操作，请确认是否退出。',
      });
    },
    /** 处理踩点组件回显 */
    handleFootprintValue(item) {
      // locationFootprint 后端返回的踩点组件唯一标识
      if (item.identification === 'locationFootprint') {

        const textValue = JSON.parse(item?.textValue || null);
        if (!isNotEmptyAny(textValue)) return;
        // 判断是否已经有踩点任务
        setIsFootprintValue(textValue.dynamicComponent_footprint_isStart === radioEnum.AGREE);

        const values = {};
        // 数据回显
        values['dynamicComponent_footprint_isStart'] = textValue.dynamicComponent_footprint_isStart;
        form.setFieldsValue(values);
        // 如果踩点任务未创建，就不往下进行
        if (textValue.dynamicComponent_footprint_isStart === radioEnum.DENY) {
          return;
        }
        // 回显踩点日期规则
        values['dynamicComponent_footprint_checkRule'] = textValue?.dynamicComponent_footprint_checkRule;
        // 回显踩点时间
        values['dynamicComponent_footprint_checkPeriod'] =
        [
          moment(textValue?.dynamicComponent_footprint_checkPeriod[0], 'HH:mm'),
          moment(textValue?.dynamicComponent_footprint_checkPeriod[1], 'HH:mm'),
        ];
        form.setFieldsValue(values);

      }
    },
    /** 计算公式的逻辑 */
    computationalLogic () {
      // 查找配置了计算公式的组件进行计算
      const calculateComs = getCalculateComs(tiledFormDataRef.current);
      // 所有的数字输入框组件
      const numInputComs = tiledFormDataRef.current.filter((item: any) => item.controlType === ControlType.INPUT_NUMBER.value);
      // 特殊控件字段：加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const specialCOms = tiledFormDataRef.current.filter((item: any) => item.identification === 'franchiseFeeYHT');
      // 计算对象：所有的数字输入框组件+加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const objCalculateComs = [...numInputComs, ...specialCOms];
      calculateHandle(objCalculateComs, calculateComs);
      // 计算的时候只设置了对应的textValue，还需要赋值到Form上
      numInputComs.forEach((item: any) => {
        const { identification, textValue } = item;
        const value = textValue ? JSON.parse(textValue)?.value : ''; // 当前组件的值
        form.setFieldValue(identification, value);
      });
    },
  });

  return (
    <div>
      <V2Drawer
        open={open}
        onClose={() => closeDrawer()}
        destroyOnClose
        className={cs('dynamicComponent', styles.formDrawer)}
        contentWrapperStyle={{
          width: '76%',
          minWidth: '1100px',
          maxWidth: 'auto',
        }}
      >
        <V2Container
          // 24px上padding
          // style={{ height: 'calc(100vh - 24px)' }}
          style={{ height: '100vh' }}
          extraContent={{
            top: (
              <div ref={topRef}>
                <Header
                  id={id}
                  chancepointAlias={chancepointAlias}
                  importPermission={importPermission}
                  onSearch={onSearch}
                  setRefreshCurrentDrawer={setRefreshCurrentDrawer}
                />
                <V2Tabs
                  items={tabs}
                  activeKey={tabActive}
                  onChange={tabChange}
                  className={styles.tabsCon}
                />
              </div>
            ),
            bottom: (
              <Footer
                id={id}
                drawerData={drawerData}
                assignedData={assignedData}
                form={form}
                formData={formData}
                isExpandStoreTaskIn={isExpandStoreTaskIn}
                needAssiocateChance={needAssiocateChance}
                supportDirectApproval={supportDirectApproval}
                isFootprintValue={isFootprintValue}
                saveSucceedRef={saveSucceedRef}
                tiledFormDataRef={tiledFormDataRef}
                setClusterHintModalOpen={setClusterHintModalOpen}
                handleExpandStoreApproved={handleExpandStoreApproved}
                update={update}
                onSearch={onSearch}
                closeDrawer={closeDrawer}
                confirmClose={confirmClose}
              />
            ),
          }}
        >
          <Main
            form={form}
            formData={formData}
            isEvaluation={isEvaluation}
            supportDirectApproval={supportDirectApproval}
            setFormData={setFormData}
            topRef={topRef}
            tabActiveRef={tabActiveRef}
            tabContentRefs={tabContentRefs}
            tabs={tabs}
            active={tabActive}
            setTabActive={setTabActive}
            // scrollAccordingTab={scrollAccordingTab}
            isLongList={isLongList}
            tiledFormDataRef={tiledFormDataRef}
            isFootprintValue={isFootprintValue}
            isAreaProtect={isAreaProtect}
            computationalLogic={computationalLogic}
            expansionTaskClusterId={expansionTaskClusterId}
            isEdit={!!id}
          />
        </V2Container>
      </V2Drawer>

      {/* 是否在所属商圈中的提示弹窗 */}
      <ClusterHintModal
        clusterHintModalOpen={clusterHintModalOpen}
        setClusterHintModalOpen={setClusterHintModalOpen}
        // form={form}
        confirmClose={confirmClose}
        tiledFormDataRef={tiledFormDataRef}
        formData={formData}
      />
    </div>
  );
};

export default ChanceCreateDrawer;
